import {NumberInput} from 'platform/components';
import {Box, HStack, Text, VStack} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';
import {z} from 'zod';

import {type KeyboardEvent} from 'react';

import {isNotNil, not} from 'ramda';

import i18n from '@dms/i18n';
import {
  EitherQuantityOrError,
  MappedBasketItem,
  useBasketItemQuantity,
  useTenant,
} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {LabourBasketItem} from '../types/LabourBasketItem';

interface AfterSalesLabourBasketItemQuantityWithPriceProps<T extends LabourBasketItem>
  extends RequiredTestIdProps {
  item: MappedBasketItem<T>;
  onQuantityChange: (itemId: string, quantity: EitherQuantityOrError) => Promise<void>;
  onTabOut?: () => void;
  hasPendingRequest: boolean;
}

export function AfterSalesLabourBasketItemQuantityWithPrice<T extends LabourBasketItem>(
  props: AfterSalesLabourBasketItemQuantityWithPriceProps<T>
) {
  const formatCurrency = useFormatCurrency();
  const tenant = useTenant();
  const {quantity, quantityError, handleQuantityChange} = useBasketItemQuantity({
    itemId: props.item.id,
    currentQuantity: props.item.quantity,
    validationSchema: quantitySchema,
    onQuantityChange: props.onQuantityChange,
    hasPendingRequest: props.hasPendingRequest,
  });

  const totalPrice = formatCurrency(
    props.item.totalPrice.withoutVat.amount ?? 0,
    props.item.totalPrice.withoutVat.currency ?? tenant.tenantCurrency,
    2
  );

  const handleQuantityKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Tab' && !e.shiftKey && props.onTabOut) {
      e.preventDefault();
      props.onTabOut();
    }
  };

  return (
    <HStack justify="space-between">
      <div role="none" onKeyDown={handleQuantityKeyDown}>
        <Box width={29}>
          <NumberInput
            isAutoselectedOnFocus
            isDisabled={props.hasPendingRequest || not(props.item.quantityEditingAllowed)}
            isInvalid={isNotNil(quantityError)}
            isStepperVisible
            value={quantity}
            errorMessage={quantityError}
            onChange={handleQuantityChange}
            data-testid={suffixTestId('quantity', props)}
          />
        </Box>
      </div>
      <VStack align="flex-end">
        <Text size="xSmall" color="tertiary">
          {i18n.t('general.labels.sellingPrice')}
        </Text>
        <Text size="small" alternative data-testid={suffixTestId('price', props)}>
          {totalPrice}
        </Text>
      </VStack>
    </HStack>
  );
}

const quantitySchema = z.number().positive();
